/*
 * Schedule's Ice Blue dialog (pages/schedule/ScheduleModal.tsx). It is portaled to <body>, outside
 * .schedule-page, so the scrim declares its own --schedule-* tokens; their dark values are in
 * appearance.css. Values follow docs/UI-GUIDE.md (the .ib-modal and .ad-btn building blocks).
 */
.schedule-modal-scrim {
  --schedule-ink: #111827;
  --schedule-muted: #606d80;
  --schedule-line: #e1ecf8;
  --schedule-line-strong: #cde1f6;
  --schedule-panel: #ffffff;
  --schedule-wash: #f0f7fe;
  --schedule-ring: #e3f0fd;
  --schedule-accent: #3d8bf5;
  --schedule-accent-soft: #79b4f8;
  --schedule-blue: #2468cc;
  --schedule-btn-fill: #2468cc;
  --schedule-btn-fill-hover: #1b4f9e;
  --schedule-btn-ink: #ffffff;
  --schedule-warn-ink: #6b4b12;
  --schedule-warn-wash: #f6e8c8;
  --schedule-warn-line: #ead6a8;
  position: fixed;
  inset: 0;
  z-index: 120;
  display: grid;
  place-items: center;
  padding: 20px;
  overflow-y: auto;
  background: rgba(31, 39, 51, 0.3);
  backdrop-filter: blur(5px);
  color: var(--schedule-ink);
  font-family: "Inter", "SF Pro Text", "Segoe UI", system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.5;
}
.schedule-modal-scrim *,
.schedule-modal-scrim *::before,
.schedule-modal-scrim *::after {
  box-sizing: border-box;
}
.schedule-modal {
  width: min(540px, 100%);
  max-height: calc(100dvh - 40px);
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 22px 24px 20px;
  overflow-y: auto;
  border: 1px solid var(--schedule-line);
  border-radius: 18px;
  background: var(--schedule-panel);
  box-shadow: 0 30px 70px rgba(31, 39, 51, 0.28);
  animation: schedule-modal-rise 200ms ease both;
}
@keyframes schedule-modal-rise {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
}
.schedule-modal :is(button, input, select, textarea) {
  font-family: inherit;
}
.schedule-modal-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}
.schedule-modal-head > div {
  flex: 1 1 auto;
  min-width: 0;
}
.schedule-modal-eyebrow {
  margin: 0 0 4px;
  color: var(--schedule-muted);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.schedule-modal-head h2 {
  margin: 0;
  color: var(--schedule-ink);
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.01em;
}
.schedule-modal-close {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--schedule-blue);
  cursor: pointer;
}
.schedule-modal-close:hover {
  background: var(--schedule-wash);
}
.schedule-modal-close svg {
  width: 16px;
  height: 16px;
}
.schedule-modal-intro,
.schedule-modal-note {
  margin: 0;
  color: var(--schedule-muted);
  font-size: 13px;
  line-height: 1.55;
}
.schedule-modal-note {
  font-size: 12.5px;
}
.schedule-modal-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.schedule-modal-choices {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  border: 0;
}
.schedule-modal-choices legend {
  width: 100%;
  margin: 0 0 6px;
  padding: 0;
  color: var(--schedule-muted);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.schedule-modal-choices label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 6px 12px;
  border: 1px solid var(--schedule-line-strong);
  border-radius: 10px;
  background: var(--schedule-panel);
  color: var(--schedule-ink);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.schedule-modal-choices label.selected {
  border-color: var(--schedule-accent-soft);
  background: var(--schedule-wash);
  color: var(--schedule-blue);
}
.schedule-modal-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 12px;
}
.schedule-modal-field {
  display: grid;
  align-content: start;
  gap: 5px;
  color: var(--schedule-muted);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.schedule-modal-field.wide,
.schedule-modal-check.wide {
  grid-column: 1 / -1;
}
.schedule-modal-field :is(input, select, textarea) {
  width: 100%;
  min-height: 36px;
  padding: 7px 9px;
  border: 1px solid var(--schedule-line-strong);
  border-radius: 8px;
  background: var(--schedule-panel);
  color: var(--schedule-ink);
  font-size: 13px;
  font-weight: 400;
  letter-spacing: normal;
  text-transform: none;
}
.schedule-modal-field :is(input, select, textarea):focus {
  outline: none;
  border-color: var(--schedule-accent-soft);
  box-shadow: 0 0 0 3px var(--schedule-ring);
}
.schedule-modal-field :is(input, select, textarea):disabled {
  background: var(--schedule-wash);
  color: var(--schedule-muted);
}
.schedule-modal-check {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--schedule-ink);
  font-size: 13px;
  font-weight: 500;
}
.schedule-modal :is(.schedule-modal-check, .schedule-modal-choices) input {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: var(--schedule-blue);
}
.schedule-modal :is(input[type="checkbox"], input[type="radio"]):focus-visible,
.schedule-modal :is(button, a):focus-visible {
  outline: 2px solid var(--schedule-accent);
  outline-offset: 2px;
}
.schedule-modal-error {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  border: 1px solid var(--schedule-warn-line);
  border-radius: 10px;
  background: var(--schedule-warn-wash);
  color: var(--schedule-warn-ink);
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.5;
}
.schedule-modal-error svg {
  width: 15px;
  height: 15px;
  margin-top: 2px;
}
.schedule-modal-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 14px;
  border-top: 1px solid var(--schedule-line);
}
.schedule-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 36px;
  padding: 6px 15px;
  border: 1px solid transparent;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition:
    background 120ms ease,
    border-color 120ms ease;
}
.schedule-btn.primary {
  border-color: var(--schedule-btn-fill);
  background: var(--schedule-btn-fill);
  color: var(--schedule-btn-ink);
}
.schedule-btn.primary:hover:not(:disabled) {
  border-color: var(--schedule-btn-fill-hover);
  background: var(--schedule-btn-fill-hover);
}
.schedule-btn.ghost {
  border-color: var(--schedule-line-strong);
  background: var(--schedule-panel);
  color: var(--schedule-ink);
}
.schedule-btn.ghost:hover:not(:disabled) {
  background: var(--schedule-wash);
}
.schedule-btn:disabled {
  border-color: var(--schedule-line);
  background: var(--schedule-wash);
  color: var(--schedule-muted);
  cursor: not-allowed;
}
.schedule-btn.sm {
  min-height: 30px;
  padding: 4px 11px;
  border-radius: 8px;
  font-size: 12.5px;
}

/* ---- Wide dialog: the intake review, and the parts Review booking shares ----------------- */
/* Review intake's own steps are in intake-review.css. What stays here is also used by the chart
   reconciler (IntakeChartReconciler.tsx) and Review booking (BookingReviewDialog.tsx). */
.schedule-modal.wide {
  width: min(960px, 100%);
}
.intake-review {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.intake-review-label {
  margin: 0;
  color: var(--schedule-muted);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.intake-review-label {
  margin-bottom: 4px;
}
.intake-review-note,
.intake-review-notice {
  margin: 0;
  color: var(--schedule-muted);
  font-size: 12.5px;
  line-height: 1.55;
}
.intake-review-notice {
  color: var(--schedule-blue);
  font-weight: 500;
}
.intake-review-caution {
  display: block;
  margin-top: 2px;
  color: var(--schedule-warn-ink);
  font-size: 11.5px;
  line-height: 1.45;
}
.intake-review-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.intake-review-linked {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.intake-review-linked p {
  margin: 0;
}
.intake-review-matches {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}
.intake-review-matches li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 12px;
  padding: 9px 0;
  border-top: 1px solid var(--schedule-line);
}
.intake-review-matches li > div {
  display: grid;
  gap: 2px;
  min-width: 0;
}
.intake-review-matches span,
.intake-review-matches small {
  color: var(--schedule-muted);
  font-size: 12px;
}
.intake-review-reconciler {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
/* "Not listed? Find the chart": any chart in the clinic by name or MRN (IntakeChartReconciler). */
.intake-review-search {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 10px;
  border-top: 1px solid var(--schedule-line);
}
.intake-review-search form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
}
.intake-review-search .schedule-modal-field {
  flex: 1 1 240px;
}
/* A card match with another date of birth (PA-10): the caution and its correction button, kept
   at the row's end like "Link this chart" (also when it wraps below the chart), the button at
   its own width. Scoped under the list so the row's `li > div` grid does not apply. */
.intake-review-matches li > .intake-review-correct {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  max-width: 260px;
  margin-left: auto;
  text-align: right;
}
/* An in-place question, safe choice first: "Correct the booking's date of birth?" and Review
   booking's "Replace the link the patient opened?" (PA-10). */
.intake-review-confirm {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  margin: 0;
  padding: 14px 16px;
  border: 1px solid var(--schedule-warn-line);
  border-radius: 12px;
  background: var(--schedule-warn-wash);
  color: var(--schedule-warn-ink);
  font-size: 13px;
  line-height: 1.5;
}
/* The question as the box's first line, not on its border: a floated legend is an ordinary
   flex item. */
.intake-review-confirm legend {
  float: left;
  width: 100%;
  margin: 0;
  padding: 0;
  color: var(--schedule-warn-ink);
  font-size: 14px;
  font-weight: 600;
}
.intake-review-confirm p,
.intake-review-confirm ul {
  margin: 0;
}
.intake-review-confirm ul {
  padding-left: 18px;
}
/* Review booking's "Ask for patient details" (PA-10), below the chart search. */
.intake-review-ask {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid var(--schedule-line);
}
.intake-review-ask h4 {
  margin: 0;
  color: var(--schedule-ink);
  font-size: 13px;
  font-weight: 600;
}
@media (max-width: 620px) {
  .schedule-modal-scrim {
    padding: 12px;
  }
  .schedule-modal {
    padding: 18px 16px 16px;
  }
  .schedule-modal-fields {
    grid-template-columns: 1fr;
  }
  .schedule-modal-actions .schedule-btn {
    flex: 1 1 auto;
  }
}
@media (prefers-reduced-motion: reduce) {
  .schedule-modal {
    animation: none;
  }
}
